export interface QuizQuestion {
  question: string
  options: string[]
  correctIndex: number
  explanation: string
}

export interface SectionData {
  id: string
  title: string
  content?: string[]
  hazards?: string[]
  measures?: string[]
  guides?: MeasureGuide[]
  quiz?: QuizQuestion[]
}

export interface MeasureGuide {
  measureIndex: number
  title: string
  steps: { step: string; detail: string; imageUrl?: string }[]
  imageUrl: string
  imageCaption: string
  dos: string[]
  donts: string[]
}

export interface ModuleData {
  id: string
  title: string
  responsible: string
  department: string
  sections: SectionData[]
}

export const module1Data: ModuleData = {
  id: 'mod-1',
  title: 'Gefährdungsbeurteilung – Büroarbeitsplatz (Softwareentwicklung)',
  responsible: 'Fabian Kleine Huxel',
  department: 'Büro / Softwareentwicklung',
  sections: [
    {
      id: 'sec-0',
      title: '1. Tätigkeiten',
      content: [
        'Softwareentwicklung an PC/Laptop',
        'Teilnahme an Meetings (digital und vor Ort)',
        'Kommunikation mit Team, Kunden und Projektbeteiligten',
        'Optional: Tätigkeit im Homeoffice',
      ],
    },
    {
      id: 'sec-1',
      title: '2.1 Bildschirmarbeit / Ergonomie',
      hazards: [
        'Muskel-Skelett-Belastungen (insbesondere Nacken-, Schulter- und Rückenbeschwerden)',
        'Visuelle Belastung (Augenermüdung, trockene Augen)',
      ],
      measures: [
        'Bereitstellung ergonomischer Arbeitsmittel (insb. höhenverstellbarer Bürostuhl mit Rückenstütze)',
        'Positionierung des Monitors auf Augenhöhe und in angemessenem Sehabstand (ca. 50–70 cm)',
        'Nutzung externer Eingabegeräte (Tastatur und Maus) bei Laptop-Arbeitsplätzen',
        'Einhaltung regelmäßiger Kurzpausen (z. B. 5–10 Minuten pro Arbeitsstunde)',
      ],
      guides: [
        {
          measureIndex: 1,
          title: 'Monitor richtig positionieren',
          steps: [
            {
              step: 'Augenhöhe bestimmen',
              detail:
                'Setzen Sie sich aufrecht hin und blicken Sie geradeaus. Die Oberkante des Monitors sollte auf Augenhöhe oder leicht darunter sein.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/1LSvsYbfeXfQdB4AsUVfNo/Gemini_Generated_Image_uqnzwpuqnzwpuqnz.png',
            },
            {
              step: 'Abstand einstellen',
              detail:
                'Positionieren Sie den Monitor in einem Abstand von 50–70 cm zu Ihren Augen (ca. eine Armlänge).',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/88ZUzU9zHV8MghEzeR76pw/Gemini_Generated_Image_mirtnxmirtnxmirt.png',
            },
            {
              step: 'Neigung anpassen',
              detail:
                'Neigen Sie den Monitor leicht nach hinten (ca. 10–20°), sodass Sie ohne Kopfneigung auf die Bildschirmmitte blicken.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/nt7mFq2mpAak7Bm1Q6LQ99/Gemini_Generated_Image_dr2icwdr2icwdr2i.png',
            },
            {
              step: 'Reflexionen vermeiden',
              detail:
                'Stellen Sie den Monitor seitlich zum Fenster auf, um Blendung und Spiegelungen zu vermeiden.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/jcVJV91SZs9GvUAhPfi1NM/Gemini_Generated_Image_pjlyupjlyupjlyup.png',
            },
          ],
          imageUrl:
            'https://cdn.magicpatterns.com/uploads/57VL94fkgd3DBswW9ccSPd/Bildschirmfoto_2026-02-25_um_08.57.40.png',
          imageCaption: 'Korrekte Monitor-Positionierung am Arbeitsplatz',
          dos: [
            'Monitor auf Augenhöhe oder leicht darunter',
            'Abstand von 50–70 cm einhalten',
            'Monitor seitlich zum Fenster positionieren',
            'Bildschirmhelligkeit an Umgebung anpassen',
          ],
          donts: [
            'Monitor zu hoch oder zu niedrig einstellen',
            'Direkt vor oder mit dem Rücken zum Fenster sitzen',
            'Bildschirm zu nah am Gesicht positionieren',
            'Spiegelnde Oberflächen hinter dem Monitor',
          ],
        },
        {
          measureIndex: 2,
          title: 'Externe Eingabegeräte richtig nutzen',
          steps: [
            {
              step: 'Externe Tastatur anschließen',
              detail:
                'Verwenden Sie eine separate Tastatur, die Sie flach vor sich auf dem Tisch positionieren können.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/rDyZNXH8MV8A9D4cwY1gRd/Gemini_Generated_Image_mkqk8pmkqk8pmkqk.png',
            },
            {
              step: 'Externe Maus verwenden',
              detail:
                'Nutzen Sie eine ergonomische Maus, die bequem in der Hand liegt. Positionieren Sie sie direkt neben der Tastatur.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/kLyQSkiDBwbCm9gLqy3fuZ/Gemini_Generated_Image_aij76haij76haij7.png',
            },
            {
              step: 'Laptop als zweiten Bildschirm nutzen',
              detail:
                'Stellen Sie den Laptop erhöht auf einen Laptopständer und nutzen Sie ihn als zusätzlichen Monitor.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/cpZyRXLuQq9JLK63JS82od/Gemini_Generated_Image_39fpl39fpl39fpl3.png',
            },
            {
              step: 'Handgelenke entlasten',
              detail:
                'Achten Sie darauf, dass Ihre Handgelenke beim Tippen gerade bleiben und nicht abknicken.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/9jwbr4eu9TnRa9ysuxRq9B/Gemini_Generated_Image_q4ztjrq4ztjrq4zt.png',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Externe+Eingabegeraete',
          imageCaption: 'Ergonomische Anordnung externer Eingabegeräte',
          dos: [
            'Separate Tastatur und Maus verwenden',
            'Laptop auf Ständer erhöhen',
            'Handgelenke gerade halten',
            'Maus nah an der Tastatur positionieren',
          ],
          donts: [
            'Direkt auf der Laptop-Tastatur tippen bei langem Arbeiten',
            'Maus weit entfernt von der Tastatur platzieren',
            'Handgelenke dauerhaft abknicken',
            'Ohne Laptopständer am Notebook arbeiten',
          ],
        },
        {
          measureIndex: 3,
          title: 'Kurzpausen richtig einplanen',
          steps: [
            {
              step: 'Timer stellen',
              detail:
                'Stellen Sie sich einen Timer auf 50–55 Minuten. Nach Ablauf machen Sie eine kurze Pause von 5–10 Minuten.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/x7VCuR1tZGXCHarKFnPfJd/Gemini_Generated_Image_8zd46u8zd46u8zd4.png',
            },
            {
              step: 'Aufstehen und bewegen',
              detail:
                'Stehen Sie auf, strecken Sie sich und gehen Sie ein paar Schritte. Holen Sie sich z. B. ein Glas Wasser.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/1MYJotccpf29K2HPQnDh6q/Gemini_Generated_Image_8k97bj8k97bj8k97.png',
            },
            {
              step: 'Augen entspannen',
              detail:
                'Schließen Sie kurz die Augen oder schauen Sie aus dem Fenster in die Ferne, um Ihre Augen zu entlasten.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/pJqkncrZcXroGWMrfD6WML/Gemini_Generated_Image_4m0u5z4m0u5z4m0u.png',
            },
            {
              step: 'Routine entwickeln',
              detail:
                'Machen Sie die Kurzpausen zur Gewohnheit. Nutzen Sie Apps oder Kalender-Erinnerungen als Hilfe.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/frQ346Ay1Yu3W5wDmmpK18/Gemini_Generated_Image_7e282y7e282y7e28.png',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Kurzpausen+Routine',
          imageCaption:
            'Regelmäßige Pausen fördern Gesundheit und Produktivität',
          dos: [
            'Alle 50–60 Minuten eine Pause einlegen',
            'Aufstehen und sich bewegen',
            'Pausen-Erinnerungen nutzen',
            'Wasser trinken und Augen entspannen',
          ],
          donts: [
            'Stundenlang ohne Pause durcharbeiten',
            'Pausen am Bildschirm verbringen (z. B. Social Media)',
            'Pausen als Zeitverschwendung betrachten',
            'Nur bei Erschöpfung pausieren',
          ],
        },
      ],
      quiz: [
        {
          question: 'Welcher Abstand zum Monitor wird empfohlen?',
          options: ['30–40 cm', '50–70 cm', '80–100 cm', '20–30 cm'],
          correctIndex: 1,
          explanation:
            'Der empfohlene Abstand beträgt 50–70 cm (ca. eine Armlänge).',
        },
        {
          question:
            'Wie oft sollten Kurzpausen bei Bildschirmarbeit eingelegt werden?',
          options: [
            'Alle 3 Stunden',
            'Nur in der Mittagspause',
            'Alle 60 Minuten (5–10 Min.)',
            'Alle 4 Stunden',
          ],
          correctIndex: 2,
          explanation:
            'Regelmäßige Kurzpausen von 5–10 Minuten pro Arbeitsstunde werden empfohlen.',
        },
        {
          question: 'Wo sollte sich die Oberkante des Monitors befinden?',
          options: [
            'Deutlich über Augenhöhe',
            'Auf Augenhöhe oder leicht darunter',
            'Auf Brusthöhe',
            'Möglichst weit unten',
          ],
          correctIndex: 1,
          explanation:
            'Die Oberkante des Monitors sollte auf Augenhöhe oder leicht darunter sein.',
        },
      ],
    },
    {
      id: 'sec-2',
      title: '2.2 Langes Sitzen / Bewegungsmangel',
      hazards: [
        'Bewegungsmangel mit möglichen Auswirkungen auf den Kreislauf',
        'Erhöhtes Risiko für Muskelverspannungen und langfristige gesundheitliche Beschwerden',
      ],
      measures: [
        'Förderung regelmäßiger Bewegungspausen während der Arbeitszeit',
        'Bereitstellung bzw. optionaler Einsatz höhenverstellbarer Schreibtische (Wechsel zwischen Sitzen und Stehen)',
        'Sensibilisierung der Mitarbeitenden für aktive Arbeitsgestaltung (z. B. kurze Wege, Positionswechsel)',
      ],
      guides: [
        {
          measureIndex: 0,
          title: 'Regelmäßige Bewegungspausen einplanen',
          steps: [
            {
              step: 'Feste Pausenzeiten setzen',
              detail:
                'Planen Sie alle 60 Minuten eine kurze Bewegungspause von 5–10 Minuten ein. Nutzen Sie Timer oder Kalender-Erinnerungen.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/ofXh4PGMMswzsjF4PUS5vs/Gemini_Generated_Image_lgxf5blgxf5blgxf.png',
            },
            {
              step: 'Aktive Pausen gestalten',
              detail:
                'Stehen Sie auf, dehnen Sie sich, gehen Sie ein paar Schritte oder machen Sie leichte Lockerungsübungen für Nacken, Schultern und Rücken.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/7bhhLYAJ6f4gGeExK7Q3et/Gemini_Generated_Image_stybztstybztstyb.png',
            },
            {
              step: 'Bewegung in den Alltag integrieren',
              detail:
                'Nutzen Sie Treppen statt Aufzüge, gehen Sie zum Drucker oder holen Sie sich ein Glas Wasser. Jede Bewegung zählt.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/wi5DoUhWFVPzEaUyteUmtE/Gemini_Generated_Image_hr6nnnhr6nnnhr6n.png',
            },
            {
              step: 'Kolleg:innen einbeziehen',
              detail:
                'Motivieren Sie sich gegenseitig zu gemeinsamen kurzen Pausen. Ein kurzer Spaziergang zusammen fördert Bewegung und Teamgeist.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/b45qCwrf2SWmg5QSwFHgif/Gemini_Generated_Image_7jmnhj7jmnhj7jmn.png',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Bewegungspausen',
          imageCaption: 'Regelmäßige Bewegungspausen beugen Beschwerden vor',
          dos: [
            'Alle 60 Minuten eine Bewegungspause einlegen',
            'Dehn- und Lockerungsübungen machen',
            'Treppen statt Aufzug nutzen',
            'Pausen-Erinnerungen im Kalender setzen',
          ],
          donts: [
            'Stundenlang ohne Unterbrechung sitzen',
            'Pausen nur am Bildschirm verbringen',
            'Bewegung auf „nach der Arbeit" verschieben',
            'Signale des Körpers (Verspannungen) ignorieren',
          ],
        },
        {
          measureIndex: 1,
          title: 'Höhenverstellbaren Schreibtisch richtig nutzen',
          steps: [
            {
              step: 'Stehhöhe einstellen',
              detail:
                'Stellen Sie den Tisch so ein, dass Ihre Ellenbogen im Stehen einen 90°-Winkel bilden und die Handgelenke gerade auf der Tischplatte aufliegen.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/obxZJa29nH4arFz77DNgtR/Gemini_Generated_Image_wv8s5twv8s5twv8s.png',
            },
            {
              step: 'Sitzhöhe einstellen',
              detail:
                'Im Sitzen sollten Ober- und Unterschenkel einen 90°-Winkel bilden. Die Füße stehen flach auf dem Boden.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/gy32eZoWB6pxG9NwaLes6A/Gemini_Generated_Image_wv8s5twv8s5twv8s.png',
            },
            {
              step: 'Regelmäßig wechseln',
              detail:
                'Wechseln Sie alle 30–60 Minuten zwischen Sitzen und Stehen. Ein guter Rhythmus ist z. B. 40 Minuten sitzen, 20 Minuten stehen.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/iVfYYyVHfg9AhU9ydAqc8f/Gemini_Generated_Image_h1ar0hh1ar0hh1ar.png',
            },
            {
              step: 'Stehposition optimieren',
              detail:
                'Verlagern Sie im Stehen gelegentlich das Gewicht von einem Bein auf das andere. Eine Anti-Ermüdungsmatte kann zusätzlich entlasten.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/8FijRUJ8Wcpywk284grdbh/Gemini_Generated_Image_h1ar0hh1ar0hh1ar.png',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Hoehenverstellbarer+Schreibtisch',
          imageCaption:
            'Wechsel zwischen Sitzen und Stehen fördert die Gesundheit',
          dos: [
            'Alle 30–60 Minuten die Position wechseln',
            'Ellenbogen im 90°-Winkel halten',
            'Anti-Ermüdungsmatte im Stehen nutzen',
            'Monitor bei Positionswechsel anpassen',
          ],
          donts: [
            'Den ganzen Tag nur stehen oder nur sitzen',
            'Tischhöhe falsch einstellen (zu hoch/niedrig)',
            'Im Stehen in eine Hohlkreuzhaltung verfallen',
            'Den Positionswechsel vergessen',
          ],
        },
        {
          measureIndex: 2,
          title: 'Aktive Arbeitsgestaltung im Alltag',
          steps: [
            {
              step: 'Kurze Wege bewusst nutzen',
              detail:
                'Gehen Sie persönlich zu Kolleg:innen, statt zu telefonieren oder zu chatten. Nutzen Sie entferntere Drucker oder Küchen.',
            },
            {
              step: 'Meetings aktiv gestalten',
              detail:
                'Führen Sie kurze Besprechungen im Stehen oder als Walking-Meetings durch. Das fördert Kreativität und Bewegung.',
            },
            {
              step: 'Arbeitsposition variieren',
              detail:
                'Wechseln Sie regelmäßig Ihre Sitzposition. Nutzen Sie verschiedene Arbeitsbereiche, wenn verfügbar (z. B. Lounge, Stehtisch).',
            },
            {
              step: 'Bewegungsroutine entwickeln',
              detail:
                'Etablieren Sie feste Gewohnheiten: z. B. morgens Treppe nehmen, mittags kurzer Spaziergang, nachmittags Dehnübungen am Platz.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Aktive+Arbeitsgestaltung',
          imageCaption: 'Kleine Veränderungen im Alltag machen den Unterschied',
          dos: [
            'Persönlich zu Kolleg:innen gehen',
            'Walking-Meetings ausprobieren',
            'Verschiedene Arbeitsbereiche nutzen',
            'Feste Bewegungsroutinen etablieren',
          ],
          donts: [
            'Den ganzen Tag am selben Platz bleiben',
            'Jede Kommunikation nur digital führen',
            'Bewegung als Zeitverlust betrachten',
            'Immer die gleiche Sitzposition beibehalten',
          ],
        },
      ],
      quiz: [
        {
          question:
            'Was sollten Sie tun, um Bewegungsmangel am Arbeitsplatz entgegenzuwirken?',
          options: [
            'Nur in der Mittagspause aufstehen',
            'Regelmäßig aufstehen, Dehnübungen und kurze Gehpausen einlegen',
            'Möglichst wenig bewegen um konzentriert zu bleiben',
            'Nur am Wochenende Sport treiben',
          ],
          correctIndex: 1,
          explanation:
            'Regelmäßige Bewegung während des Arbeitstages – Aufstehen, Dehnen, kurze Gehpausen – beugt Beschwerden durch langes Sitzen vor.',
        },
        {
          question:
            'Welche Auswirkung kann dauerhaftes Sitzen ohne Bewegung haben?',
          options: [
            'Bessere Konzentration',
            'Kreislaufprobleme und Muskel-Skelett-Beschwerden',
            'Höhere Produktivität',
            'Keine negativen Auswirkungen',
          ],
          correctIndex: 1,
          explanation:
            'Bewegungsmangel kann zu Kreislaufproblemen und Muskel-Skelett-Beschwerden führen.',
        },
      ],
    },
    {
      id: 'sec-3',
      title: '2.3 Psychische Belastungen',
      hazards: [
        'Zeitdruck durch Projektfristen und Deadlines',
        'Hohe kognitive Anforderungen (Konzentration, Problemlösung)',
        'Kommunikationsbelastung bei parallelen Projekten',
      ],
      measures: [
        'Realistische und transparente Projekt- und Ressourcenplanung',
        'Förderung einer offenen und klaren Teamkommunikation',
        'Möglichkeit flexibler Arbeitszeiten zur individuellen Belastungssteuerung',
        'Regelmäßige Abstimmungen (z. B. Reviews, Retrospektiven) zur frühzeitigen Problemerkennung',
      ],
      guides: [
        {
          measureIndex: 0,
          title: 'Realistische Projekt- und Ressourcenplanung',
          steps: [
            {
              step: 'Aufwand ehrlich einschätzen',
              detail:
                'Schätzen Sie den Aufwand für Aufgaben realistisch ein. Nutzen Sie Erfahrungswerte aus vergangenen Projekten und planen Sie Puffer für Unvorhergesehenes ein.',
            },
            {
              step: 'Prioritäten klar setzen',
              detail:
                'Definieren Sie gemeinsam im Team, welche Aufgaben Priorität haben. Nutzen Sie Methoden wie die Eisenhower-Matrix oder MoSCoW, um Wichtiges von Dringendem zu trennen.',
            },
            {
              step: 'Kapazitäten transparent machen',
              detail:
                'Machen Sie die aktuelle Auslastung im Team sichtbar (z. B. über ein Board oder Tool). So können Überlastungen frühzeitig erkannt und umverteilt werden.',
            },
            {
              step: 'Nein sagen lernen',
              detail:
                'Kommunizieren Sie offen, wenn Kapazitätsgrenzen erreicht sind. Ein rechtzeitiges „Das schaffen wir so nicht" schützt vor Überlastung und schlechter Qualität.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Projektplanung',
          imageCaption: 'Realistische Planung schützt vor Überlastung',
          dos: [
            'Pufferzeiten in die Planung einbauen',
            'Erfahrungswerte für Schätzungen nutzen',
            'Auslastung im Team sichtbar machen',
            'Frühzeitig kommunizieren, wenn es eng wird',
          ],
          donts: [
            'Unrealistische Deadlines akzeptieren ohne Rücksprache',
            'Überstunden als Dauerlösung einplanen',
            'Aufwand systematisch unterschätzen',
            'Probleme erst ansprechen, wenn es zu spät ist',
          ],
        },
        {
          measureIndex: 1,
          title: 'Offene und klare Teamkommunikation fördern',
          steps: [
            {
              step: 'Regelmäßige Check-ins einführen',
              detail:
                'Führen Sie kurze tägliche oder wöchentliche Check-ins ein (z. B. 15-Minuten-Standup). So bleiben alle informiert und Probleme werden früh sichtbar.',
            },
            {
              step: 'Psychologische Sicherheit schaffen',
              detail:
                'Fördern Sie eine Kultur, in der Fehler offen angesprochen werden dürfen. Niemand sollte Angst haben, Probleme oder Unsicherheiten zu benennen.',
            },
            {
              step: 'Kommunikationskanäle klären',
              detail:
                'Definieren Sie klare Regeln: Was wird per Chat, E-Mail oder Meeting besprochen? Reduzieren Sie unnötige Benachrichtigungen und Meetings.',
            },
            {
              step: 'Feedback-Kultur etablieren',
              detail:
                'Geben und empfangen Sie regelmäßig konstruktives Feedback. Nutzen Sie z. B. Retrospektiven oder 1-on-1-Gespräche als festen Rahmen.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Teamkommunikation',
          imageCaption: 'Offene Kommunikation stärkt das Team',
          dos: [
            'Regelmäßige kurze Check-ins durchführen',
            'Fehler als Lernchance betrachten',
            'Klare Kommunikationsregeln vereinbaren',
            'Konstruktives Feedback geben und annehmen',
          ],
          donts: [
            'Probleme verschweigen oder herunterspielens',
            'Nur per E-Mail kommunizieren bei Konflikten',
            'Feedback nur im Jahresgespräch geben',
            'Ständige Erreichbarkeit erwarten',
          ],
        },
        {
          measureIndex: 2,
          title: 'Flexible Arbeitszeiten sinnvoll nutzen',
          steps: [
            {
              step: 'Persönlichen Rhythmus finden',
              detail:
                'Identifizieren Sie Ihre produktivsten Zeiten (z. B. morgens für konzentrierte Arbeit). Legen Sie anspruchsvolle Aufgaben in Ihre Leistungshochs.',
            },
            {
              step: 'Kernzeiten respektieren',
              detail:
                'Halten Sie sich an vereinbarte Kernzeiten für Meetings und Teamabstimmungen. Außerhalb dieser Zeiten gestalten Sie Ihren Tag flexibel.',
            },
            {
              step: 'Pausen bewusst einplanen',
              detail:
                'Nutzen Sie die Flexibilität für echte Erholung: ein Spaziergang mittags, Sport am Nachmittag oder eine längere Mittagspause bei Bedarf.',
            },
            {
              step: 'Feierabend einhalten',
              detail:
                'Setzen Sie sich ein klares Arbeitsende. Flexible Zeiten bedeuten nicht „immer arbeiten". Trennen Sie Arbeit und Freizeit bewusst.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Flexible+Arbeitszeiten',
          imageCaption: 'Flexibilität braucht klare Grenzen',
          dos: [
            'Produktive Phasen für konzentrierte Arbeit nutzen',
            'Kernzeiten für Teamarbeit einhalten',
            'Pausen und Erholung bewusst einplanen',
            'Klares Arbeitsende setzen',
          ],
          donts: [
            'Flexibilität als Dauerverfügbarkeit missverstehen',
            'Pausen ausfallen lassen, weil man „flexibel" ist',
            'Kernzeiten regelmäßig ignorieren',
            'Arbeit und Freizeit vermischen',
          ],
        },
        {
          measureIndex: 3,
          title: 'Regelmäßige Abstimmungen etablieren',
          steps: [
            {
              step: 'Retrospektiven einführen',
              detail:
                'Führen Sie alle 2–4 Wochen eine Retrospektive durch. Besprechen Sie: Was lief gut? Was können wir verbessern? Welche konkreten Maßnahmen nehmen wir mit?',
            },
            {
              step: 'Reviews nutzen',
              detail:
                'Zeigen Sie regelmäßig Arbeitsergebnisse im Team (z. B. Sprint Reviews). Das schafft Transparenz und ermöglicht frühzeitiges Feedback.',
            },
            {
              step: 'Probleme frühzeitig ansprechen',
              detail:
                'Nutzen Sie Abstimmungen aktiv, um Hindernisse zu benennen. Je früher ein Problem erkannt wird, desto leichter ist die Lösung.',
            },
            {
              step: 'Ergebnisse dokumentieren',
              detail:
                'Halten Sie Beschlüsse und Maßnahmen schriftlich fest. So gehen Vereinbarungen nicht verloren und können nachverfolgt werden.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Regelmaessige+Abstimmungen',
          imageCaption: 'Regelmäßige Abstimmungen fördern Transparenz',
          dos: [
            'Retrospektiven alle 2–4 Wochen durchführen',
            'Arbeitsergebnisse regelmäßig im Team zeigen',
            'Hindernisse offen benennen',
            'Beschlüsse schriftlich festhalten',
          ],
          donts: [
            'Meetings ohne klare Agenda abhalten',
            'Probleme erst bei Eskalation ansprechen',
            'Retrospektiven als Schuldzuweisung nutzen',
            'Vereinbarungen nicht nachverfolgen',
          ],
        },
      ],
      quiz: [
        {
          question:
            'Was sollten Sie tun, wenn Sie sich dauerhaft überlastet fühlen?',
          options: [
            'Einfach weiterarbeiten und hoffen, dass es besser wird',
            'Das Problem frühzeitig mit der Führungskraft besprechen',
            'Sich krankschreiben lassen ohne Rücksprache',
            'Die Arbeit an Kollegen abgeben',
          ],
          correctIndex: 1,
          explanation:
            'Bei dauerhafter Überlastung sollten Sie frühzeitig das Gespräch mit Ihrer Führungskraft suchen, um gemeinsam Lösungen zu finden.',
        },
        {
          question:
            'Welche Maßnahme hilft, unklare Zuständigkeiten im Team zu vermeiden?',
          options: [
            'Jeder macht was er will',
            'Klare Aufgabenverteilung und regelmäßige Abstimmungen',
            'Möglichst wenig kommunizieren',
            'Alle Aufgaben einer Person zuweisen',
          ],
          correctIndex: 1,
          explanation:
            'Klare Aufgabenverteilung und regelmäßige Team-Abstimmungen schaffen Transparenz und vermeiden Überlastung einzelner Personen.',
        },
      ],
    },
    {
      id: 'sec-4',
      title: '2.4 Arbeitsplatzgestaltung / Ordnung',
      hazards: [
        'Stolper- und Sturzgefahr durch lose, frei liegende oder schlecht verlegte Kabel (z. B. im Fußbereich oder an Laufwegen)',
        'Gefährdung durch unübersichtliche Arbeitsplätze, insbesondere bei herumliegenden Arbeitsmitteln oder Materialien',
        'Erhöhtes Unfallrisiko beim Aufstehen, Bewegen oder Wechsel des Arbeitsplatzes',
        'Eingeschränkte Flucht- und Verkehrswege durch abgestellte Gegenstände oder unsachgemäße Lagerung',
        'Sekundäre Gefährdungen, z. B. Beschädigung von Geräten oder Kabeln mit möglichen Folgerisiken',
      ],
      measures: [
        'Einsatz eines strukturierten Kabelmanagements (z. B. Kabelkanäle, Kabelbinder, Untertischführungen)',
        'Vermeidung von Kabeln in Lauf- und Verkehrsbereichen',
        'Sicherstellung ausreichend freier Bewegungsflächen am Arbeitsplatz',
        'Klare Regelungen zur Arbeitsplatzordnung („Clean Desk"-Prinzip)',
        'Sicherstellung, dass Flucht- und Rettungswege jederzeit frei zugänglich sind',
        'Regelmäßige Sichtkontrollen der Arbeitsplätze durch Mitarbeitende und ggf. Führungskräfte',
      ],
      guides: [
        {
          measureIndex: 0,
          title: 'Strukturiertes Kabelmanagement umsetzen',
          steps: [
            {
              step: 'Kabelführung installieren',
              detail:
                'Nutzen Sie Kabelkanäle, Kabelbinder oder Untertischführungen, um alle Leitungen sicher und ordentlich zu verlegen. Kabel sollten nie frei auf dem Boden liegen.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/1ntk4FtKp64beCtQQ38csC/Gemini_Generated_Image_txu2tntxu2tntxu2.png',
            },
            {
              step: 'Laufwege freihalten',
              detail:
                'Stellen Sie sicher, dass keine Kabel über Laufwege oder Verkehrsbereiche führen. Verlegen Sie Kabel entlang von Wänden oder unter Schreibtischen.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/3rg9CRGxBJMFjecRomqdBR/Gemini_Generated_Image_gsuinwgsuinwgsui.png',
            },
            {
              step: 'Regelmäßig kontrollieren',
              detail:
                'Prüfen Sie Ihr Kabelmanagement regelmäßig, besonders nach Umbauten oder neuen Geräten. Beschädigte Kabel sofort melden oder austauschen.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/kz8QoP5BEgFNUoTdQv73A2/Gemini_Generated_Image_tu2lzztu2lzztu2l.png',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Kabelmanagement',
          imageCaption: 'Ordentliches Kabelmanagement verhindert Stolperfallen',
          dos: [
            'Kabelkanäle und Kabelbinder verwenden',
            'Kabel entlang von Wänden oder unter Tischen verlegen',
            'Beschädigte Kabel sofort melden',
            'Nach Umbauten Kabelführung prüfen',
          ],
          donts: [
            'Kabel frei über den Boden legen',
            'Kabel über Laufwege führen',
            'Beschädigte Kabel weiter verwenden',
            'Kabel mit Klebeband provisorisch befestigen',
          ],
        },
        {
          measureIndex: 3,
          title: 'Clean Desk – Arbeitsplatz ordentlich halten',
          steps: [
            {
              step: 'Arbeitsfläche aufräumen',
              detail:
                'Halten Sie Ihren Schreibtisch frei von unnötigen Gegenständen. Nur das, was Sie aktuell brauchen, gehört auf die Arbeitsfläche.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/gXhYN1PSU9US9HG6j5nWKy/Gemini_Generated_Image_qdwdusqdwdusqdwd.png',
            },
            {
              step: 'Taschen und Gegenstände verstauen',
              detail:
                'Stellen Sie Taschen, Kartons oder persönliche Gegenstände nicht in Laufwegen ab. Nutzen Sie Schränke, Regale oder Ablagefächer.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/b1cdkymAKNrzLf6HAUauhw/Gemini_Generated_Image_qv4igwqv4igwqv4i.png',
            },
            {
              step: 'Arbeitsplatz am Ende des Tages ordnen',
              detail:
                'Hinterlassen Sie Ihren Arbeitsplatz geordnet: Dokumente ablegen, Tasse wegräumen, Stuhl heranschieben. So starten Sie und andere am nächsten Tag sicher.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/b1VQFpgEyc3NYUKVJ4A2rB/Gemini_Generated_Image_bw51vibw51vibw51_(1).png',
            },
            {
              step: 'Auffälligkeiten melden',
              detail:
                'Melden Sie Stolperstellen, beschädigte Kabel oder andere Gefahrenquellen umgehend. Beseitigen Sie kleine Probleme sofort, sofern gefahrlos möglich.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Clean+Desk',
          imageCaption:
            'Ein aufgeräumter Arbeitsplatz ist ein sicherer Arbeitsplatz',
          dos: [
            'Nur aktuell benötigte Dinge auf dem Schreibtisch',
            'Taschen und Gegenstände in Schränken verstauen',
            'Arbeitsplatz am Tagesende aufräumen',
            'Gefahrenstellen sofort melden',
          ],
          donts: [
            'Gegenstände in Laufwegen abstellen',
            'Arbeitsplatz unaufgeräumt hinterlassen',
            'Stolperstellen ignorieren',
            'Beschädigte Gegenstände weiter nutzen',
          ],
        },
        {
          measureIndex: 4,
          title: 'Flucht- und Rettungswege freihalten',
          steps: [
            {
              step: 'Fluchtwege kennen',
              detail:
                'Machen Sie sich mit den Flucht- und Rettungswegen in Ihrem Gebäude vertraut. Achten Sie auf die grünen Notausgang-Schilder und Fluchtpläne.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/rJzsnDnkVHYV9hcfBenQEA/unnamed_(1).jpg',
            },
            {
              step: 'Wege freihalten',
              detail:
                'Stellen Sie keine Gegenstände, Kartons oder Möbel in Flure, Treppenhäuser oder vor Notausgänge. Diese müssen jederzeit frei passierbar sein.',
            },
            {
              step: 'Türen nicht blockieren',
              detail:
                'Halten Sie Brandschutztüren und Notausgänge niemals mit Keilen oder Gegenständen offen. Sie müssen im Notfall automatisch schließen können.',
            },
            {
              step: 'Regelmäßig prüfen',
              detail:
                'Achten Sie im Alltag darauf, ob Fluchtwege frei sind. Melden Sie Blockierungen oder fehlende Beschilderung umgehend an Ihre Führungskraft oder den Sicherheitsbeauftragten.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Fluchtwege',
          imageCaption: 'Freie Fluchtwege retten im Notfall Leben',
          dos: [
            'Fluchtpläne und Notausgänge kennen',
            'Flure und Treppenhäuser freihalten',
            'Blockierungen sofort melden',
            'Brandschutztüren geschlossen halten',
          ],
          donts: [
            'Gegenstände vor Notausgänge stellen',
            'Brandschutztüren aufkeilen',
            'Fluchtwege als Lagerplatz nutzen',
            'Fehlende Beschilderung ignorieren',
          ],
        },
      ],
      quiz: [
        {
          question: 'Was sollten Sie mit losen Kabeln am Boden tun?',
          options: [
            'Ignorieren, solange niemand stolpert',
            'Mit Kabelkanälen oder Kabelbrücken sichern',
            'Einfach zur Seite schieben',
            'Abschneiden',
          ],
          correctIndex: 1,
          explanation:
            'Lose Kabel sollten mit Kabelkanälen oder Kabelbrücken gesichert werden, um Stolpergefahren zu vermeiden.',
        },
        {
          question: 'Was tun bei verschütteter Flüssigkeit auf dem Boden?',
          options: [
            'Warten bis es trocknet',
            'Sofort aufwischen und ggf. Warnschild aufstellen',
            'Einen Umweg nehmen',
            'Nichts, das ist nicht mein Problem',
          ],
          correctIndex: 1,
          explanation:
            'Verschüttete Flüssigkeiten sollten sofort aufgewischt werden, um Rutschgefahr zu vermeiden.',
        },
        {
          question: 'Warum müssen Fluchtwege immer freigehalten werden?',
          options: [
            'Weil es besser aussieht',
            'Damit Rettungskräfte schnell zum Unfallort gelangen und Personen evakuiert werden können',
            'Weil der Chef es so will',
            'Nur bei Feueralarm relevant',
          ],
          correctIndex: 1,
          explanation:
            'Freie Fluchtwege sind lebenswichtig, damit im Notfall eine schnelle Evakuierung möglich ist und Rettungskräfte Zugang haben.',
        },
      ],
    },
    {
      id: 'sec-5',
      title: '2.5 Elektrosicherheit',
      hazards: [
        'Stromschläge durch beschädigte Kabel oder defekte Geräte',
        'Brandgefahr durch Überlastung von Steckdosen oder Mehrfachsteckdosen',
        'Kurzschlüsse durch unsachgemäße Nutzung elektrischer Geräte',
        'Folgeschäden durch nicht erkannte Defekte (z. B. Datenverlust, Geräteausfall)',
      ],
      measures: [
        'Keine beschädigten Kabel oder Geräte verwenden – sichtbare Schäden stellen ein Sicherheitsrisiko dar',
        'Keine eigenständigen Reparaturen durchführen – Reparaturen nur durch qualifiziertes Fachpersonal',
        'Steckdosen und Mehrfachsteckdosen nicht überlasten – keine Kaskadierung von Mehrfachsteckdosen',
        'Leistungsstarke Geräte nur an geeigneten Anschlüssen betreiben',
        'Auffälligkeiten und Defekte sofort melden (z. B. ungewöhnliche Geräusche, Hitzeentwicklung, Geruch oder Funktionsstörungen)',
        'Mitarbeitende werden regelmäßig über Gefahren elektrischer Energie informiert',
        'Der Grundsatz „Im Zweifel: nicht benutzen, sondern melden" wird klar kommuniziert',
        'Zuständigkeiten und Meldewege werden eindeutig festgelegt',
        'Nur einwandfreie und geprüfte Geräte verwenden – Geräte dürfen keine sichtbaren Schäden aufweisen und müssen betriebsbereit sein',
        'Keine Manipulation oder Improvisation an elektrischen Geräten – keine provisorischen Reparaturen, keine geöffneten Gehäuse, keine Zweckentfremdung',
        'Nur zugelassene und bereitgestellte Geräte verwenden – private oder nicht freigegebene Geräte nur nach entsprechender Prüfung einsetzen',
        'Geräte bestimmungsgemäß verwenden – Nutzung ausschließlich im vorgesehenen Einsatzbereich',
        'Auffälligkeiten oder Defekte unverzüglich melden – Nutzung bis zur Klärung einstellen',
        'Elektrische Geräte werden regelmäßig durch qualifizierte Fachkräfte geprüft (DGUV V3, intern oder extern)',
        'Prüfungen erfolgen nach festgelegten Intervallen und werden dokumentiert',
        'Mitarbeitende unterstützen die Prüfungen, indem sie Geräte zugänglich machen und keine Prüfkennzeichnungen entfernen oder beschädigen',
      ],
      guides: [
        {
          measureIndex: 0,
          title: 'Beschädigtes Kabel oder defektes Gerät erkannt',
          steps: [
            {
              step: 'Gerät außer Betrieb nehmen',
              detail:
                'Stellen Sie das betroffene Gerät sofort außer Betrieb. Ziehen Sie wenn möglich den Stecker – aber nur, wenn dies gefahrlos möglich ist.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/pShu23h2tsmuVKoP6W8ToX/Gemini_Generated_Image_s8agwns8agwns8ag.png',
            },
            {
              step: 'Nutzung einstellen',
              detail:
                'Verwenden Sie das beschädigte Gerät oder Kabel nicht weiter. Kennzeichnen Sie es als defekt, damit andere es nicht versehentlich nutzen.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/fwELXrUZaSAhR8WeZRLKiy/Gemini_Generated_Image_ng4ax7ng4ax7ng4a.png',
            },
            {
              step: 'Defekt umgehend melden',
              detail:
                'Melden Sie den Defekt sofort an Ihre Führungskraft oder den zuständigen Sicherheitsbeauftragten. Dokumentieren Sie Art und Ort des Schadens.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/1gtvSgUYe94A29orrPKARS/Gemini_Generated_Image_omtus8omtus8omtu.png',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Kabelsicherheit',
          imageCaption:
            'Beschädigte Kabel sofort melden und nicht weiter verwenden',
          dos: [
            'Sichtbare Schäden sofort melden',
            'Gerät außer Betrieb nehmen',
            'Defekte Geräte kennzeichnen',
            'Nur geprüfte Geräte verwenden',
          ],
          donts: [
            'Beschädigte Kabel weiter verwenden',
            'Eigenständig Reparaturen durchführen',
            'Defekte ignorieren oder aufschieben',
            'Provisorische Reparaturen mit Klebeband',
          ],
        },
        {
          measureIndex: 1,
          title: 'Gerät funktioniert ungewöhnlich',
          steps: [
            {
              step: 'Gerät ausschalten',
              detail:
                'Schalten Sie das Gerät sofort aus, wenn es flackert, ungewöhnlich heiß wird oder seltsame Geräusche macht – aber nur, wenn dies gefahrlos möglich ist.',
            },
            {
              step: 'Nutzung einstellen',
              detail:
                'Verwenden Sie das Gerät nicht weiter und informieren Sie Kolleginnen und Kollegen in der Nähe über die Auffälligkeit.',
            },
            {
              step: 'Meldung an zuständige Stelle',
              detail:
                'Informieren Sie umgehend Ihre Führungskraft oder den Sicherheitsbeauftragten. Beschreiben Sie die Auffälligkeit möglichst genau.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Geraetesicherheit',
          imageCaption: 'Ungewöhnliches Verhalten von Geräten sofort melden',
          dos: [
            'Gerät sofort ausschalten',
            'Kollegen informieren',
            'Auffälligkeit genau beschreiben',
            'Zuständige Stelle kontaktieren',
          ],
          donts: [
            'Gerät trotz Auffälligkeiten weiter nutzen',
            'Abwarten ob es sich von selbst löst',
            'Selbst am Gerät herumschrauben',
            'Auffälligkeiten verschweigen',
          ],
        },
        {
          measureIndex: 2,
          title: 'Steckdosen und Mehrfachsteckdosen prüfen',
          steps: [
            {
              step: 'Angeschlossene Geräte prüfen',
              detail:
                'Prüfen Sie, wie viele Geräte an einer Steckdose oder Mehrfachsteckdose angeschlossen sind. Achten Sie auf Überlastungszeichen wie Wärmeentwicklung.',
            },
            {
              step: 'Kaskadierung vermeiden',
              detail:
                'Stecken Sie niemals Mehrfachsteckdosen hintereinander. Dies erhöht die Brandgefahr erheblich.',
            },
            {
              step: 'Gegebenenfalls reduzieren',
              detail:
                'Reduzieren Sie die Anzahl angeschlossener Geräte oder stimmen Sie eine geeignete Lösung mit der zuständigen Stelle ab.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Steckdosensicherheit',
          imageCaption: 'Steckdosen nicht überlasten – keine Kaskadierung',
          dos: [
            'Regelmäßig Steckdosenbelastung prüfen',
            'Leistungsstarke Geräte einzeln anschließen',
            'Bei Unsicherheit nachfragen',
            'Überlastungszeichen ernst nehmen',
          ],
          donts: [
            'Mehrfachsteckdosen hintereinander stecken',
            'Zu viele Geräte an einer Steckdose',
            'Wärmeentwicklung an Steckdosen ignorieren',
            'Provisorische Verlängerungen dauerhaft nutzen',
          ],
        },
        {
          measureIndex: 3,
          title: 'Geräte sicher verwenden – Verhaltensregeln',
          steps: [
            {
              step: 'Gerät vor Nutzung prüfen',
              detail:
                'Prüfen Sie vor jeder Nutzung, ob das Gerät einwandfrei und ohne sichtbare Schäden ist. Achten Sie auf Risse, freiliegende Kabel, lockere Stecker oder fehlende Prüfplaketten.',
            },
            {
              step: 'Keine Manipulation oder Improvisation',
              detail:
                'Führen Sie niemals provisorische Reparaturen durch, öffnen Sie keine Gehäuse und zweckentfremden Sie keine elektrischen Geräte. Manipulationen gefährden Sie und andere.',
            },
            {
              step: 'Nur zugelassene Geräte verwenden',
              detail:
                'Verwenden Sie ausschließlich vom Unternehmen bereitgestellte oder freigegebene Geräte. Private Geräte dürfen nur nach entsprechender Prüfung und Freigabe eingesetzt werden.',
            },
            {
              step: 'Bestimmungsgemäße Verwendung sicherstellen',
              detail:
                'Nutzen Sie jedes Gerät ausschließlich im vorgesehenen Einsatzbereich und gemäß der Bedienungsanleitung. Unsachgemäße Nutzung kann zu Kurzschlüssen oder Bränden führen.',
            },
            {
              step: 'Auffälligkeiten sofort melden',
              detail:
                'Stellen Sie bei einem Gerät Auffälligkeiten fest (z. B. ungewöhnliche Geräusche, Geruch, Hitze), stellen Sie die Nutzung sofort ein und melden Sie den Vorfall an Ihre Führungskraft.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Geraetesicherheit+Regeln',
          imageCaption: 'Nur einwandfreie und zugelassene Geräte verwenden',
          dos: [
            'Geräte vor Nutzung auf Schäden prüfen',
            'Nur freigegebene Geräte verwenden',
            'Geräte bestimmungsgemäß nutzen',
            'Auffälligkeiten sofort melden',
          ],
          donts: [
            'Provisorische Reparaturen durchführen',
            'Gehäuse öffnen oder manipulieren',
            'Private Geräte ohne Freigabe nutzen',
            'Geräte zweckentfremden',
          ],
        },
        {
          measureIndex: 4,
          title: 'DGUV V3 Prüfungen – So unterstützen Sie',
          steps: [
            {
              step: 'Prüfintervalle kennen',
              detail:
                'Elektrische Geräte werden regelmäßig durch qualifizierte Fachkräfte geprüft (DGUV Vorschrift 3). Die Prüfungen erfolgen nach festgelegten Intervallen und werden vollständig dokumentiert.',
            },
            {
              step: 'Geräte zugänglich machen',
              detail:
                'Stellen Sie sicher, dass alle elektrischen Geräte an Ihrem Arbeitsplatz für Prüfungen zugänglich sind. Räumen Sie bei angekündigten Prüfungen den Zugang zu Steckdosen und Geräten frei.',
            },
            {
              step: 'Prüfkennzeichnungen beachten',
              detail:
                'Entfernen oder beschädigen Sie niemals Prüfplaketten oder Prüfkennzeichnungen an Geräten. Diese dokumentieren die letzte Prüfung und das nächste Prüfdatum.',
            },
            {
              step: 'Nicht geprüfte Geräte melden',
              detail:
                'Fällt Ihnen auf, dass ein Gerät keine gültige Prüfplakette hat oder das Prüfdatum überschritten ist, melden Sie dies umgehend. Nutzen Sie das Gerät bis zur Klärung nicht weiter.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=DGUV+V3+Pruefung',
          imageCaption: 'Regelmäßige Prüfungen sichern den sicheren Betrieb',
          dos: [
            'Geräte für Prüfungen zugänglich machen',
            'Prüfplaketten beachten und erhalten',
            'Fehlende Prüfkennzeichnungen melden',
            'Prüftermine unterstützen',
          ],
          donts: [
            'Prüfplaketten entfernen oder beschädigen',
            'Geräte ohne gültige Prüfung nutzen',
            'Prüfungen behindern oder verzögern',
            'Überfällige Prüftermine ignorieren',
          ],
        },
      ],
      quiz: [
        {
          question:
            'Was tun Sie, wenn Sie ein beschädigtes Kabel an einem Gerät entdecken?',
          options: [
            'Mit Klebeband reparieren',
            'Weiter verwenden, solange es funktioniert',
            'Sofort melden und das Gerät nicht weiter verwenden',
            'Kabel selbst austauschen',
          ],
          correctIndex: 2,
          explanation:
            'Beschädigte Kabel sofort melden und das Gerät außer Betrieb nehmen. Eigenständige Reparaturen sind nicht erlaubt.',
        },
        {
          question:
            'Darf man Mehrfachsteckdosen hintereinander stecken (kaskadieren)?',
          options: [
            'Ja, wenn nicht zu viele Geräte angeschlossen sind',
            'Nein, das erhöht die Brandgefahr erheblich',
            'Ja, das ist kein Problem',
            'Nur mit Genehmigung der Führungskraft',
          ],
          correctIndex: 1,
          explanation:
            'Kaskadierung von Mehrfachsteckdosen ist verboten, da sie die Brandgefahr erheblich erhöht.',
        },
        {
          question:
            'Was bedeutet die Prüfplakette auf einem elektrischen Gerät?',
          options: [
            'Das Gerät ist neu gekauft',
            'Das Gerät wurde geprüft – Datum der letzten und nächsten Prüfung',
            'Das Gerät gehört der Firma',
            'Das Gerät darf nicht benutzt werden',
          ],
          correctIndex: 1,
          explanation:
            'Die Prüfplakette dokumentiert die letzte Prüfung und das nächste Prüfdatum gemäß DGUV V3.',
        },
      ],
    },
    {
      id: 'sec-6',
      title: '2.6 Homeoffice (sofern zutreffend)',
      hazards: [
        'Ergonomische Fehlbelastungen durch ungeeignete Arbeitsplätze (z. B. Arbeiten am Küchentisch, Sofa oder Bett)',
        'Muskel-Skelett-Beschwerden durch falsche Sitzhaltung oder unzureichende Ausstattung',
        'Visuelle Belastung durch ungünstige Bildschirmposition',
        'Vermischung von Arbeits- und Privatleben mit erhöhter psychischer Belastung',
        'Fehlende Pausen oder Überarbeitung durch flexible Arbeitsgestaltung',
        'Soziale Isolation durch fehlenden persönlichen Austausch',
      ],
      measures: [
        'Nutzung eines geeigneten Tisches und eines ergonomischen Stuhls',
        'Bildschirm auf Augenhöhe und in ausreichendem Abstand positionieren',
        'Keine dauerhafte Arbeit auf ungeeigneten Flächen (Sofa, Bett o. Ä.)',
        'Bei Laptop-Nutzung externe Eingabegeräte (Tastatur/Maus) verwenden',
        'Einhaltung definierter Arbeitszeiten und Pausenregelungen',
        'Einen festen Arbeitsplatz im Homeoffice einrichten (möglichst räumlich getrennt)',
        'Arbeitsbeginn und Arbeitsende bewusst festlegen',
        'Regelmäßige Pausen und Bewegungseinheiten in den Arbeitsalltag integrieren',
        'Arbeits- und Privatbereiche klar trennen',
        'Für ausreichende Beleuchtung und störungsarme Arbeitsbedingungen sorgen',
        'Regelmäßigen Austausch mit dem Team aufrechterhalten',
        'Mitarbeitende werden über ergonomische Anforderungen im Homeoffice informiert',
        'Homeoffice unterliegt denselben Arbeitsschutzanforderungen wie Büroarbeitsplätze',
        'Leitlinien oder Checklisten zur Einrichtung des Heimarbeitsplatzes werden bereitgestellt',
      ],
      guides: [
        {
          measureIndex: 0,
          title: 'Ergonomischen Homeoffice-Arbeitsplatz einrichten',
          steps: [
            {
              step: 'Geeigneten Arbeitsplatz wählen',
              detail:
                'Richten Sie einen festen Arbeitsplatz ein – idealerweise an einem Schreibtisch in einem separaten Raum. Vermeiden Sie dauerhaftes Arbeiten am Küchentisch, auf dem Sofa oder im Bett.',
              imageUrl:
                'https://cdn.magicpatterns.com/uploads/jLKwxkjgFH3hLEDS4vtsFa/Gemini_Generated_Image_wfjdrdwfjdrdwfjd.png',
            },
            {
              step: 'Stuhl und Tisch prüfen',
              detail:
                'Nutzen Sie einen ergonomischen Bürostuhl mit Rückenstütze und einen Tisch in passender Höhe. Ihre Unterarme sollten waagerecht auf der Tischplatte aufliegen können.',
            },
            {
              step: 'Bildschirm richtig positionieren',
              detail:
                'Stellen Sie den Bildschirm auf Augenhöhe und in einem Abstand von 50–70 cm auf. Bei Laptop-Nutzung verwenden Sie einen Laptopständer, um den Bildschirm anzuheben.',
            },
            {
              step: 'Externe Eingabegeräte nutzen',
              detail:
                'Schließen Sie bei Laptop-Arbeit eine externe Tastatur und Maus an. So vermeiden Sie eine gebeugte Haltung und entlasten Nacken und Schultern.',
            },
            {
              step: 'Beleuchtung optimieren',
              detail:
                'Sorgen Sie für ausreichende Beleuchtung. Positionieren Sie den Bildschirm seitlich zum Fenster, um Blendung zu vermeiden. Ergänzen Sie bei Bedarf eine Schreibtischlampe.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Homeoffice+Ergonomie',
          imageCaption:
            'Ein ergonomisch eingerichteter Homeoffice-Arbeitsplatz',
          dos: [
            'Festen Arbeitsplatz mit Schreibtisch einrichten',
            'Ergonomischen Stuhl verwenden',
            'Bildschirm auf Augenhöhe positionieren',
            'Externe Tastatur und Maus nutzen',
          ],
          donts: [
            'Dauerhaft auf Sofa oder Bett arbeiten',
            'Laptop ohne Ständer auf dem Schoß nutzen',
            'In dunklen oder schlecht beleuchteten Räumen arbeiten',
            'Ohne Rückenstütze auf einem Küchenstuhl sitzen',
          ],
        },
        {
          measureIndex: 1,
          title: 'Arbeitszeit und Pausen strukturieren',
          steps: [
            {
              step: 'Feste Arbeitszeiten definieren',
              detail:
                'Legen Sie klare Arbeitszeiten fest und kommunizieren Sie diese an Ihr Team. Ein bewusster Arbeitsbeginn und ein definiertes Arbeitsende helfen, Überarbeitung zu vermeiden.',
            },
            {
              step: 'Regelmäßige Pausen einplanen',
              detail:
                'Planen Sie bewusst Kurzpausen ein (z. B. 5–10 Minuten pro Stunde). Stehen Sie auf, bewegen Sie sich und blicken Sie in die Ferne, um Augen und Körper zu entlasten.',
            },
            {
              step: 'Bewegung integrieren',
              detail:
                'Bauen Sie Bewegungseinheiten in Ihren Arbeitsalltag ein – z. B. kurze Dehnübungen, ein Spaziergang in der Mittagspause oder Stehphasen beim Telefonieren.',
            },
            {
              step: 'Arbeits- und Privatbereich trennen',
              detail:
                'Trennen Sie Arbeit und Freizeit bewusst: Arbeiten Sie nur am vorgesehenen Arbeitsplatz und räumen Sie Arbeitsmaterialien nach Feierabend weg. So schaffen Sie klare Grenzen.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Arbeitszeit+Pausen',
          imageCaption:
            'Klare Struktur schützt vor Überarbeitung und Isolation',
          dos: [
            'Feste Arbeitszeiten einhalten',
            'Regelmäßige Pausen machen',
            'Bewegung in den Alltag einbauen',
            'Arbeitsplatz nach Feierabend verlassen',
          ],
          donts: [
            'Ohne Pausen durcharbeiten',
            'Arbeits- und Freizeit vermischen',
            'Abends noch E-Mails beantworten',
            'Sich vom Team isolieren',
          ],
        },
        {
          measureIndex: 2,
          title: 'Teamkontakt und Wohlbefinden sichern',
          steps: [
            {
              step: 'Regelmäßigen Austausch pflegen',
              detail:
                'Halten Sie regelmäßigen Kontakt zu Ihrem Team – z. B. über tägliche kurze Check-ins, virtuelle Kaffeepausen oder wöchentliche Teammeetings.',
            },
            {
              step: 'Kommunikationskanäle aktiv nutzen',
              detail:
                'Nutzen Sie Chat, Video und Telefon aktiv, um den persönlichen Austausch zu ersetzen. Scheuen Sie sich nicht, bei Fragen oder Problemen Kontakt aufzunehmen.',
            },
            {
              step: 'Auf eigenes Wohlbefinden achten',
              detail:
                'Achten Sie bewusst auf Ihr körperliches und psychisches Wohlbefinden. Bei anhaltender Belastung, Isolation oder Beschwerden sprechen Sie Ihre Führungskraft oder den Betriebsarzt an.',
            },
            {
              step: 'Arbeitsschutz auch im Homeoffice ernst nehmen',
              detail:
                'Denken Sie daran: Homeoffice unterliegt denselben Arbeitsschutzanforderungen wie der Büroarbeitsplatz. Nutzen Sie bereitgestellte Checklisten zur Einrichtung Ihres Arbeitsplatzes.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Teamkontakt+Homeoffice',
          imageCaption: 'Regelmäßiger Austausch beugt Isolation vor',
          dos: [
            'Tägliche Check-ins mit dem Team',
            'Video-Calls statt nur Chat nutzen',
            'Bei Belastung frühzeitig Hilfe suchen',
            'Checklisten zur Arbeitsplatzeinrichtung nutzen',
          ],
          donts: [
            'Sich vom Team abkapseln',
            'Probleme oder Belastungen verschweigen',
            'Arbeitsschutz im Homeoffice ignorieren',
            'Auf ergonomische Einrichtung verzichten',
          ],
        },
      ],
      quiz: [
        {
          question: 'Wo sollten Sie im Homeoffice NICHT dauerhaft arbeiten?',
          options: [
            'Am Schreibtisch mit ergonomischem Stuhl',
            'Auf dem Sofa oder im Bett',
            'An einem höhenverstellbaren Tisch',
            'In einem separaten Arbeitszimmer',
          ],
          correctIndex: 1,
          explanation:
            'Dauerhaftes Arbeiten auf dem Sofa oder im Bett führt zu ergonomischen Fehlbelastungen und ist zu vermeiden.',
        },
        {
          question:
            'Was sollten Sie bei Laptop-Arbeit im Homeoffice zusätzlich nutzen?',
          options: [
            'Einen größeren Laptop',
            'Externe Tastatur und Maus',
            'Nur den Laptop-Bildschirm',
            'Ein Tablet als Zweitgerät',
          ],
          correctIndex: 1,
          explanation:
            'Externe Tastatur und Maus entlasten Nacken und Schultern und ermöglichen eine ergonomische Haltung.',
        },
        {
          question:
            'Gelten im Homeoffice dieselben Arbeitsschutzanforderungen wie im Büro?',
          options: [
            'Nein, zu Hause gelten keine Regeln',
            'Ja, Homeoffice unterliegt denselben Anforderungen',
            'Nur teilweise',
            'Nur wenn der Arbeitgeber es verlangt',
          ],
          correctIndex: 1,
          explanation:
            'Homeoffice unterliegt denselben Arbeitsschutzanforderungen wie der Büroarbeitsplatz.',
        },
      ],
    },
    {
      id: 'sec-7',
      title: '2.7 Grundlagen IT-Sicherheit',
      hazards: [
        'Datenverlust durch unsichere Passwörter oder fehlende Backups',
        'Phishing-Angriffe über gefälschte E-Mails, Links oder Anhänge',
        'Malware-Infektionen durch unsichere Downloads oder externe Datenträger',
        'Unbefugter Zugriff auf Systeme durch ungesperrte Geräte oder geteilte Zugangsdaten',
        'Social Engineering – Manipulation durch Täuschung (z. B. Anrufe, gefälschte Identitäten)',
        'Datenschutzverletzungen durch unsachgemäßen Umgang mit personenbezogenen Daten',
      ],
      measures: [
        'Sichere Passwörter verwenden und regelmäßig ändern – Mindestens 12 Zeichen, Kombination aus Groß-/Kleinbuchstaben, Zahlen und Sonderzeichen',
        'Zwei-Faktor-Authentifizierung (2FA) aktivieren, wo immer möglich',
        'Bildschirm bei Verlassen des Arbeitsplatzes immer sperren (Windows: Win+L, Mac: Ctrl+Cmd+Q)',
        'Keine Passwörter weitergeben oder auf Zetteln notieren',
        'E-Mails und Links kritisch prüfen – bei Verdacht auf Phishing nicht klicken, sondern melden',
        'Keine unbekannten USB-Sticks oder externe Datenträger anschließen',
        'Software-Updates und Sicherheitspatches zeitnah installieren',
        'Vertrauliche Daten nur über verschlüsselte Kanäle übertragen',
        'Sensible Dokumente nicht offen am Arbeitsplatz liegen lassen (Clean Desk)',
        'Verdächtige Vorfälle sofort an die IT-Abteilung oder den IT-Sicherheitsbeauftragten melden',
        'Regelmäßige Sensibilisierung der Mitarbeitenden zu aktuellen Bedrohungen',
        'Nutzung privater Geräte und Software nur nach Freigabe durch die IT',
      ],
      guides: [
        {
          measureIndex: 0,
          title: 'Sichere Passwörter erstellen und verwalten',
          steps: [
            {
              step: 'Starkes Passwort erstellen',
              detail:
                'Verwenden Sie mindestens 12 Zeichen mit einer Mischung aus Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen. Vermeiden Sie persönliche Informationen wie Geburtstage oder Namen.',
            },
            {
              step: 'Für jeden Dienst ein eigenes Passwort',
              detail:
                'Nutzen Sie niemals dasselbe Passwort für mehrere Accounts. Wird ein Passwort kompromittiert, sind sonst alle Zugänge gefährdet.',
            },
            {
              step: 'Passwort-Manager verwenden',
              detail:
                'Nutzen Sie einen vom Unternehmen bereitgestellten Passwort-Manager, um Ihre Passwörter sicher zu speichern. So müssen Sie sich nur ein Master-Passwort merken.',
            },
            {
              step: 'Zwei-Faktor-Authentifizierung aktivieren',
              detail:
                'Aktivieren Sie 2FA überall, wo es möglich ist. Selbst wenn Ihr Passwort gestohlen wird, schützt der zweite Faktor (z. B. Authenticator-App) Ihren Zugang.',
            },
            {
              step: 'Passwörter niemals teilen',
              detail:
                'Geben Sie Ihre Passwörter niemals an Kollegen, Vorgesetzte oder externe Personen weiter – auch nicht auf Nachfrage. Kein seriöser Dienst fragt nach Ihrem Passwort.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Passwortsicherheit',
          imageCaption: 'Starke Passwörter sind die erste Verteidigungslinie',
          dos: [
            'Mindestens 12 Zeichen verwenden',
            'Passwort-Manager nutzen',
            '2FA aktivieren',
            'Für jeden Dienst ein eigenes Passwort',
          ],
          donts: [
            'Passwörter auf Zetteln notieren',
            'Gleiche Passwörter mehrfach verwenden',
            'Passwörter per E-Mail oder Chat teilen',
            'Einfache Passwörter wie "123456" oder "Passwort"',
          ],
        },
        {
          measureIndex: 1,
          title: 'Phishing-Angriffe erkennen und abwehren',
          steps: [
            {
              step: 'Absender genau prüfen',
              detail:
                'Kontrollieren Sie die E-Mail-Adresse des Absenders sorgfältig. Phishing-Mails verwenden oft leicht abgewandelte Adressen (z. B. "support@amaz0n.com" statt "support@amazon.com").',
            },
            {
              step: 'Links vor dem Klicken prüfen',
              detail:
                'Fahren Sie mit der Maus über Links, ohne zu klicken. Prüfen Sie, ob die angezeigte URL zur erwarteten Website passt. Bei Zweifeln: nicht klicken.',
            },
            {
              step: 'Anhänge mit Vorsicht behandeln',
              detail:
                'Öffnen Sie keine Anhänge von unbekannten Absendern. Auch bei bekannten Absendern: Wenn der Anhang unerwartet kommt, fragen Sie beim Absender nach.',
            },
            {
              step: 'Dringlichkeit hinterfragen',
              detail:
                'Phishing-Mails erzeugen oft künstlichen Zeitdruck ("Ihr Konto wird gesperrt!", "Sofort handeln!"). Lassen Sie sich nicht unter Druck setzen – prüfen Sie in Ruhe.',
            },
            {
              step: 'Verdächtige E-Mails melden',
              detail:
                'Melden Sie verdächtige E-Mails sofort an die IT-Abteilung. Leiten Sie die E-Mail weiter oder nutzen Sie den internen Meldeweg. Löschen Sie die E-Mail nicht eigenständig.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Phishing+Schutz',
          imageCaption: 'Phishing erkennen – nicht klicken, sondern melden',
          dos: [
            'Absenderadresse genau prüfen',
            'Links vor dem Klicken kontrollieren',
            'Verdächtige E-Mails an IT melden',
            'Bei Zweifeln nachfragen',
          ],
          donts: [
            'Auf Links in verdächtigen E-Mails klicken',
            'Anhänge von unbekannten Absendern öffnen',
            'Persönliche Daten per E-Mail preisgeben',
            'Sich von Zeitdruck unter Druck setzen lassen',
          ],
        },
        {
          measureIndex: 2,
          title: 'Arbeitsplatz und Geräte absichern',
          steps: [
            {
              step: 'Bildschirm immer sperren',
              detail:
                'Sperren Sie Ihren Bildschirm jedes Mal, wenn Sie den Arbeitsplatz verlassen – auch nur kurz. Windows: Win+L, Mac: Ctrl+Cmd+Q. So verhindern Sie unbefugten Zugriff.',
            },
            {
              step: 'Software aktuell halten',
              detail:
                'Installieren Sie Sicherheitsupdates und Patches zeitnah, sobald sie verfügbar sind. Veraltete Software enthält bekannte Sicherheitslücken, die Angreifer ausnutzen.',
            },
            {
              step: 'Keine unbekannten USB-Geräte anschließen',
              detail:
                'Schließen Sie niemals gefundene oder unbekannte USB-Sticks an Ihren Rechner an. Diese können Schadsoftware enthalten, die sich automatisch installiert.',
            },
            {
              step: 'Clean Desk beachten',
              detail:
                'Lassen Sie vertrauliche Dokumente, Notizen oder Zugangsdaten nicht offen am Arbeitsplatz liegen. Schließen Sie sensible Unterlagen in Schränken ein.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=IT+Arbeitsplatz+Sicherheit',
          imageCaption: 'Bildschirm sperren und Arbeitsplatz absichern',
          dos: [
            'Bildschirm bei jedem Verlassen sperren',
            'Updates sofort installieren',
            'Vertrauliche Dokumente wegschließen',
            'Nur freigegebene Geräte anschließen',
          ],
          donts: [
            'Bildschirm ungesperrt lassen',
            'Updates aufschieben oder ignorieren',
            'Unbekannte USB-Sticks verwenden',
            'Passwörter auf dem Schreibtisch notieren',
          ],
        },
        {
          measureIndex: 3,
          title: 'Sicherheitsvorfälle richtig melden',
          steps: [
            {
              step: 'Vorfall erkennen',
              detail:
                'Achten Sie auf Anzeichen eines Sicherheitsvorfalls: unerwartete Pop-ups, langsamer Rechner, unbekannte Programme, verdächtige E-Mails oder Anrufe, die nach Zugangsdaten fragen.',
            },
            {
              step: 'Ruhe bewahren und nicht selbst handeln',
              detail:
                'Versuchen Sie nicht, das Problem selbst zu beheben. Trennen Sie das Gerät nicht vom Netzwerk, es sei denn, die IT-Abteilung weist Sie an. Bewahren Sie Ruhe.',
            },
            {
              step: 'IT-Abteilung sofort informieren',
              detail:
                'Melden Sie den Vorfall unverzüglich an die IT-Abteilung oder den IT-Sicherheitsbeauftragten. Beschreiben Sie, was passiert ist: Was haben Sie gesehen? Was haben Sie angeklickt?',
            },
            {
              step: 'Vorfall dokumentieren',
              detail:
                'Notieren Sie Zeitpunkt, betroffenes Gerät und was genau passiert ist. Screenshots können hilfreich sein. Diese Informationen helfen der IT bei der schnellen Analyse.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Sicherheitsvorfall+melden',
          imageCaption: 'Schnelles Melden verhindert größere Schäden',
          dos: [
            'Vorfälle sofort an die IT melden',
            'Ruhe bewahren und dokumentieren',
            'Screenshots als Beweise sichern',
            'Anweisungen der IT befolgen',
          ],
          donts: [
            'Vorfälle verschweigen oder ignorieren',
            'Selbst versuchen das Problem zu lösen',
            'Verdächtige E-Mails eigenständig löschen',
            'Weiterarbeiten als wäre nichts passiert',
          ],
        },
      ],
      quiz: [
        {
          question: 'Was tun Sie bei einer verdächtigen E-Mail?',
          options: [
            'Anhang öffnen um zu prüfen',
            'Auf den Link klicken und schauen was passiert',
            'Nicht klicken, sondern an die IT-Abteilung melden',
            'E-Mail sofort löschen',
          ],
          correctIndex: 2,
          explanation:
            'Verdächtige E-Mails nicht öffnen oder anklicken, sondern sofort an die IT-Abteilung melden.',
        },
        {
          question: 'Wie lang sollte ein sicheres Passwort mindestens sein?',
          options: ['6 Zeichen', '8 Zeichen', '12 Zeichen', '4 Zeichen'],
          correctIndex: 2,
          explanation:
            'Ein sicheres Passwort sollte mindestens 12 Zeichen lang sein und Groß-/Kleinbuchstaben, Zahlen und Sonderzeichen enthalten.',
        },
        {
          question:
            'Was sollten Sie tun, wenn Sie Ihren Arbeitsplatz verlassen?',
          options: [
            'Nichts, ich bin ja gleich wieder da',
            'Bildschirm sperren (Win+L / Ctrl+Cmd+Q)',
            'Nur den Monitor ausschalten',
            'Computer herunterfahren',
          ],
          correctIndex: 1,
          explanation:
            'Sperren Sie Ihren Bildschirm jedes Mal, wenn Sie den Arbeitsplatz verlassen – auch nur kurz.',
        },
      ],
    },
    {
      id: 'sec-8',
      title: '2.8 Verhalten bei Verletzungen',
      hazards: [
        'Schnittverletzungen (z. B. durch Papier, Cuttermesser oder scharfe Kanten)',
        'Stoß- und Quetschverletzungen (z. B. an Schubladen, Türen oder Möbelkanten)',
        'Sturzverletzungen (z. B. durch Stolpern über Kabel, Ausrutschen auf glatten Böden)',
        'Verbrennungen oder Verbrühungen (z. B. durch heiße Getränke oder Geräte)',
        'Elektrische Verletzungen (z. B. durch defekte Geräte)',
        'Verschlimmerung von Verletzungen durch falsche oder fehlende Erstversorgung',
      ],
      measures: [
        'Standort des Erste-Hilfe-Kastens und der Ersthelfer im Gebäude kennen',
        'Notrufnummern und interne Meldewege kennen (Notruf: 112, intern: Empfang/Führungskraft)',
        'Jede Verletzung – auch Bagatellverletzungen – im Verbandbuch dokumentieren',
        'Ersthelfer bei Bedarf sofort hinzuziehen',
        'Bei schweren Verletzungen sofort den Notruf (112) wählen',
        'Verletzte Person nicht allein lassen, bis Hilfe eintrifft',
        'Unfallstelle sichern, um Folgeunfälle zu vermeiden',
        'Regelmäßige Auffrischung der Erste-Hilfe-Kenntnisse',
        'Flucht- und Rettungswege freihalten für schnellen Zugang von Rettungskräften',
        'Führungskraft über jeden Arbeitsunfall informieren',
      ],
      guides: [
        {
          measureIndex: 0,
          title: 'Erste Hilfe bei leichten Verletzungen',
          steps: [
            {
              step: 'Ruhe bewahren und Situation einschätzen',
              detail:
                'Bewahren Sie Ruhe und verschaffen Sie sich einen Überblick. Handelt es sich um eine leichte Verletzung (z. B. kleiner Schnitt, Prellung) oder ist professionelle Hilfe nötig?',
            },
            {
              step: 'Erste-Hilfe-Kasten holen',
              detail:
                'Holen Sie den nächsten Erste-Hilfe-Kasten. Die Standorte sind durch weiße Schilder mit grünem Kreuz gekennzeichnet. Fragen Sie Kollegen, wenn Sie den Standort nicht kennen.',
            },
            {
              step: 'Wunde versorgen',
              detail:
                'Reinigen Sie kleine Wunden vorsichtig und decken Sie sie mit einem Pflaster oder sterilen Verband ab. Verwenden Sie bei der Versorgung Einmalhandschuhe aus dem Erste-Hilfe-Kasten.',
            },
            {
              step: 'Im Verbandbuch dokumentieren',
              detail:
                'Tragen Sie jede Verletzung – auch Bagatellverletzungen – im Verbandbuch ein: Datum, Uhrzeit, Art der Verletzung, durchgeführte Maßnahmen. Dies ist gesetzlich vorgeschrieben.',
            },
            {
              step: 'Führungskraft informieren',
              detail:
                'Informieren Sie Ihre Führungskraft über den Vorfall. Auch kleine Verletzungen können auf Gefährdungen hinweisen, die behoben werden müssen.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Erste+Hilfe+leicht',
          imageCaption:
            'Auch kleine Verletzungen richtig versorgen und dokumentieren',
          dos: [
            'Einmalhandschuhe verwenden',
            'Wunde reinigen und abdecken',
            'Im Verbandbuch dokumentieren',
            'Führungskraft informieren',
          ],
          donts: [
            'Verletzungen ignorieren oder verschweigen',
            'Wunden ohne Handschuhe versorgen',
            'Dokumentation im Verbandbuch vergessen',
            'Verschmutzte Pflaster oder Verbände verwenden',
          ],
        },
        {
          measureIndex: 1,
          title: 'Verhalten bei schweren Verletzungen / Notfällen',
          steps: [
            {
              step: 'Eigenschutz beachten',
              detail:
                'Bringen Sie sich zuerst selbst in Sicherheit. Prüfen Sie, ob von der Unfallstelle weitere Gefahren ausgehen (z. B. Strom, rutschiger Boden, herabfallende Gegenstände).',
            },
            {
              step: 'Notruf absetzen (112)',
              detail:
                'Wählen Sie sofort den Notruf 112. Geben Sie an: Wo ist es passiert? Was ist passiert? Wie viele Verletzte? Welche Verletzungen? Warten Sie auf Rückfragen der Leitstelle.',
            },
            {
              step: 'Ersthelfer hinzuziehen',
              detail:
                'Rufen Sie sofort den nächsten betrieblichen Ersthelfer. Die Namen und Standorte der Ersthelfer sind am Erste-Hilfe-Aushang im Gebäude zu finden.',
            },
            {
              step: 'Verletzte Person betreuen',
              detail:
                'Bleiben Sie bei der verletzten Person und leisten Sie nach Ihren Möglichkeiten Erste Hilfe. Sprechen Sie beruhigend mit der Person und lassen Sie sie nicht allein.',
            },
            {
              step: 'Unfallstelle sichern',
              detail:
                'Sichern Sie die Unfallstelle, um Folgeunfälle zu verhindern. Warnen Sie andere Personen und halten Sie den Zugang für Rettungskräfte frei.',
            },
            {
              step: 'Rettungskräfte einweisen',
              detail:
                'Sorgen Sie dafür, dass jemand die Rettungskräfte am Gebäudeeingang empfängt und zum Unfallort führt. Öffnen Sie Türen und räumen Sie Hindernisse aus dem Weg.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Notfall+Verhalten',
          imageCaption:
            'Im Notfall: Eigenschutz → Notruf → Erste Hilfe → Sichern',
          dos: [
            'Zuerst Eigenschutz beachten',
            'Notruf 112 sofort wählen',
            'Bei der verletzten Person bleiben',
            'Rettungskräfte einweisen',
          ],
          donts: [
            'Sich selbst in Gefahr bringen',
            'Verletzte Person allein lassen',
            'Schwer Verletzte bewegen (außer bei Lebensgefahr)',
            'Panik verbreiten',
          ],
        },
        {
          measureIndex: 2,
          title: 'Ersthelfer und Rettungskette kennen',
          steps: [
            {
              step: 'Ersthelfer im Gebäude kennen',
              detail:
                'Machen Sie sich mit den betrieblichen Ersthelfern vertraut. Ihre Namen und Standorte finden Sie am Erste-Hilfe-Aushang, meist in der Nähe des Erste-Hilfe-Kastens.',
            },
            {
              step: 'Erste-Hilfe-Material lokalisieren',
              detail:
                'Merken Sie sich den Standort des nächsten Erste-Hilfe-Kastens, des Defibrilators (AED) und der Krankentrage. Die Standorte sind durch grüne Schilder mit weißem Kreuz markiert.',
            },
            {
              step: 'Notrufnummern kennen',
              detail:
                'Notruf: 112 (Feuerwehr/Rettungsdienst), Polizei: 110, Giftnotruf: Nummer am Erste-Hilfe-Aushang. Speichern Sie die interne Notfallnummer in Ihrem Telefon.',
            },
            {
              step: 'Rettungswege kennen',
              detail:
                'Machen Sie sich mit den Flucht- und Rettungswegen vertraut. Rettungskräfte müssen schnell zum Unfallort gelangen können – halten Sie Wege und Zugänge immer frei.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Rettungskette',
          imageCaption: 'Die Rettungskette: Jedes Glied zählt',
          dos: [
            'Ersthelfer und deren Standorte kennen',
            'Erste-Hilfe-Kasten-Standort merken',
            'Notrufnummern griffbereit haben',
            'Rettungswege freihalten',
          ],
          donts: [
            'Sich nicht um Ersthelfer-Standorte kümmern',
            'Notrufnummern nicht kennen',
            'Rettungswege blockieren',
            'Erste-Hilfe-Material zweckentfremden',
          ],
        },
        {
          measureIndex: 3,
          title: 'Unfälle korrekt dokumentieren und melden',
          steps: [
            {
              step: 'Verbandbuch-Eintrag erstellen',
              detail:
                'Dokumentieren Sie jeden Vorfall im Verbandbuch: Datum, Uhrzeit, Name der verletzten Person, Art und Ort der Verletzung, durchgeführte Erste-Hilfe-Maßnahmen, Name des Ersthelfers.',
            },
            {
              step: 'Führungskraft informieren',
              detail:
                'Melden Sie jeden Arbeitsunfall – auch Bagatellverletzungen – an Ihre direkte Führungskraft. Diese entscheidet über weitere Schritte und leitet ggf. eine Unfallanzeige ein.',
            },
            {
              step: 'Bei schweren Unfällen: Unfallanzeige',
              detail:
                'Bei Arbeitsunfällen mit mehr als 3 Tagen Arbeitsunfähigkeit muss eine Unfallanzeige an die Berufsgenossenschaft erfolgen. Ihre Führungskraft kümmert sich darum – unterstützen Sie mit Informationen.',
            },
            {
              step: 'Ursachen analysieren und Maßnahmen ableiten',
              detail:
                'Nach jedem Unfall wird geprüft, wie er entstanden ist und wie er künftig vermieden werden kann. Ihre Beobachtungen und Hinweise sind dabei wertvoll – teilen Sie diese aktiv mit.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Unfall+Dokumentation',
          imageCaption: 'Lückenlose Dokumentation schützt alle Beteiligten',
          dos: [
            'Jeden Vorfall im Verbandbuch eintragen',
            'Führungskraft sofort informieren',
            'Beobachtungen zur Unfallursache teilen',
            'Bei Bedarf Unfallanzeige unterstützen',
          ],
          donts: [
            'Verletzungen nicht dokumentieren',
            'Unfälle verschweigen',
            'Verbandbuch-Einträge aufschieben',
            'Unfallursachen ignorieren',
          ],
        },
      ],
      quiz: [
        {
          question:
            'Was ist die Notrufnummer für Rettungsdienst und Feuerwehr?',
          options: ['110', '112', '118', '911'],
          correctIndex: 1,
          explanation:
            'Die Notrufnummer 112 gilt europaweit für Rettungsdienst und Feuerwehr.',
        },
        {
          question:
            'Wo muss jede Verletzung am Arbeitsplatz dokumentiert werden?',
          options: [
            'In einer E-Mail an den Chef',
            'Im Verbandbuch',
            'Auf einem Notizzettel',
            'Nirgends, wenn es nur eine Kleinigkeit ist',
          ],
          correctIndex: 1,
          explanation:
            'Jede Verletzung – auch Bagatellverletzungen – muss im Verbandbuch dokumentiert werden. Das ist gesetzlich vorgeschrieben.',
        },
        {
          question: 'Was ist der erste Schritt bei einem schweren Unfall?',
          options: [
            'Sofort den Verletzten bewegen',
            'Fotos machen',
            'Eigenschutz beachten und Situation einschätzen',
            'Weiterarbeiten und später melden',
          ],
          correctIndex: 2,
          explanation:
            'Eigenschutz hat immer Vorrang. Erst wenn Sie sich selbst in Sicherheit gebracht haben, können Sie anderen helfen.',
        },
      ],
    },
    {
      id: 'sec-9',
      title: '2.9 Allgemeine Grundregeln',
      hazards: [
        'Erhöhtes Unfallrisiko durch Zeitdruck und Unachtsamkeit',
        'Gefährdungen durch nicht gemeldete Mängel oder Gefahrenquellen',
        'Fehlverhalten durch mangelnde Umsetzung von Unterweisungsinhalten',
        'Unkenntnis über Zuständigkeiten, Meldewege und Notfallprozesse',
      ],
      measures: [
        'Sicherheit geht immer vor Schnelligkeit – keine Abkürzungen bei Sicherheitsregeln',
        'Probleme, Gefahren oder Mängel sofort melden – nicht abwarten oder ignorieren',
        'Unterweisungsinhalte aktiv im Arbeitsalltag umsetzen – nicht nur unterschreiben',
        'Eigenverantwortung übernehmen – jeder trägt zur Sicherheit am Arbeitsplatz bei',
        'Kolleginnen und Kollegen auf unsicheres Verhalten aufmerksam machen',
        'Im Zweifel immer nachfragen – lieber einmal zu viel als einmal zu wenig',
        'Persönliche Schutzausrüstung (sofern erforderlich) konsequent tragen',
        'Arbeitsplatz ordentlich und sicher hinterlassen',
      ],
      guides: [
        {
          measureIndex: 0,
          title: 'Sicherheit geht vor Schnelligkeit',
          steps: [
            {
              step: 'Zeitdruck erkennen',
              detail:
                'Achten Sie bewusst darauf, wenn Zeitdruck entsteht – z. B. durch enge Deadlines, parallele Aufgaben oder unerwartete Anforderungen. Zeitdruck ist einer der häufigsten Auslöser für Unfälle.',
            },
            {
              step: 'Sicherheitsregeln einhalten – ohne Ausnahme',
              detail:
                'Auch unter Zeitdruck gelten alle Sicherheitsregeln uneingeschränkt. Keine Abkürzungen, keine „schnellen Lösungen", die Sicherheitsstandards umgehen.',
            },
            {
              step: 'Prioritäten richtig setzen',
              detail:
                'Wenn Sicherheit und Geschwindigkeit in Konflikt geraten, hat Sicherheit immer Vorrang. Sprechen Sie mit Ihrer Führungskraft, wenn Zeitvorgaben die Sicherheit gefährden.',
            },
            {
              step: 'Konzentration bewahren',
              detail:
                'Arbeiten Sie aufmerksam und konzentriert. Vermeiden Sie Multitasking bei sicherheitsrelevanten Tätigkeiten. Pausen helfen, die Konzentration aufrechtzuerhalten.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Sicherheit+vor+Schnelligkeit',
          imageCaption: 'Sicherheit hat immer Vorrang – ohne Ausnahme',
          dos: [
            'Sicherheitsregeln immer einhalten',
            'Bei Zeitdruck Führungskraft ansprechen',
            'Konzentriert und aufmerksam arbeiten',
            'Pausen zur Erholung nutzen',
          ],
          donts: [
            'Sicherheitsregeln unter Zeitdruck umgehen',
            'Abkürzungen bei Sicherheitsmaßnahmen nehmen',
            'Risiken eingehen um schneller zu sein',
            'Unkonzentriert arbeiten',
          ],
        },
        {
          measureIndex: 1,
          title: 'Probleme und Gefahren sofort melden',
          steps: [
            {
              step: 'Gefährdung erkennen',
              detail:
                'Achten Sie auf mögliche Gefahrenquellen: lose Kabel, defekte Geräte, rutschige Böden, blockierte Fluchtwege, ungewöhnliche Gerüche oder Geräusche. Jeder kann Gefahren erkennen.',
            },
            {
              step: 'Sofort handeln – nicht abwarten',
              detail:
                'Melden Sie erkannte Gefahren sofort. Warten Sie nicht darauf, dass jemand anderes es tut. Kleine Mängel können schnell zu ernsthaften Unfällen führen.',
            },
            {
              step: 'Richtige Stelle informieren',
              detail:
                'Melden Sie den Mangel an Ihre Führungskraft, den Sicherheitsbeauftragten oder die zuständige Fachabteilung (z. B. IT, Haustechnik). Beschreiben Sie Ort und Art der Gefährdung.',
            },
            {
              step: 'Wenn möglich: Gefahr absichern',
              detail:
                'Können Sie die Gefahr gefahrlos absichern (z. B. verschüttete Flüssigkeit aufwischen, Hinweisschild aufstellen), tun Sie dies. Bringen Sie sich dabei nicht selbst in Gefahr.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Gefahren+melden',
          imageCaption: 'Hinschauen – Melden – Handeln',
          dos: [
            'Gefahren sofort melden',
            'Konkret beschreiben was und wo',
            'Wenn möglich gefahrlos absichern',
            'Auch kleine Mängel ernst nehmen',
          ],
          donts: [
            'Gefahren ignorieren oder aufschieben',
            'Darauf warten dass andere melden',
            'Sich selbst in Gefahr bringen',
            'Mängel als unwichtig abtun',
          ],
        },
        {
          measureIndex: 2,
          title: 'Unterweisung aktiv umsetzen',
          steps: [
            {
              step: 'Inhalte verstehen – nicht nur unterschreiben',
              detail:
                'Eine Unterweisung ist nur wirksam, wenn Sie die Inhalte verstanden haben. Fragen Sie nach, wenn etwas unklar ist. Es gibt keine dummen Fragen bei der Arbeitssicherheit.',
            },
            {
              step: 'Im Arbeitsalltag anwenden',
              detail:
                'Setzen Sie die gelernten Maßnahmen konsequent im Alltag um: ergonomisches Arbeiten, Bildschirmpausen, Kabelmanagement, Meldepflichten. Wissen allein schützt nicht – nur Handeln.',
            },
            {
              step: 'Kollegen unterstützen',
              detail:
                'Machen Sie Kolleginnen und Kollegen freundlich auf unsicheres Verhalten aufmerksam. Sicherheit ist Teamarbeit – jeder trägt Verantwortung für sich und andere.',
            },
            {
              step: 'Eigenverantwortung übernehmen',
              detail:
                'Sie sind mitverantwortlich für Ihre Sicherheit und die Ihrer Kollegen. Übernehmen Sie aktiv Verantwortung: Arbeitsplatz ordentlich halten, Regeln einhalten, im Zweifel nachfragen.',
            },
          ],
          imageUrl:
            'https://placehold.co/600x400/e8f5e9/4a4a5a?text=Unterweisung+umsetzen',
          imageCaption: 'Wissen schützt nur, wenn es angewendet wird',
          dos: [
            'Bei Unklarheiten nachfragen',
            'Gelerntes im Alltag umsetzen',
            'Kollegen auf Risiken hinweisen',
            'Eigenverantwortung übernehmen',
          ],
          donts: [
            'Unterweisung nur als Formalität sehen',
            'Inhalte nach der Unterschrift vergessen',
            'Unsicheres Verhalten bei anderen ignorieren',
            'Verantwortung auf andere abschieben',
          ],
        },
      ],
      quiz: [
        {
          question: 'Was hat am Arbeitsplatz immer Vorrang?',
          options: [
            'Schnelligkeit',
            'Produktivität',
            'Sicherheit',
            'Kundenwünsche',
          ],
          correctIndex: 2,
          explanation:
            'Sicherheit geht immer vor Schnelligkeit – keine Abkürzungen bei Sicherheitsregeln, auch nicht unter Zeitdruck.',
        },
        {
          question: 'Was tun Sie, wenn Sie eine Gefahrenquelle erkennen?',
          options: [
            'Abwarten ob jemand anderes es meldet',
            'Sofort melden und wenn möglich gefahrlos absichern',
            'Ignorieren, wenn es mich nicht betrifft',
            'Erst am nächsten Tag melden',
          ],
          correctIndex: 1,
          explanation:
            'Erkannte Gefahren sofort melden – nicht abwarten. Kleine Mängel können schnell zu ernsthaften Unfällen führen.',
        },
        {
          question: 'Was bedeutet es, eine Unterweisung „aktiv umzusetzen"?',
          options: [
            'Nur die Unterschrift leisten',
            'Die Inhalte im Arbeitsalltag konsequent anwenden',
            'Das Dokument aufbewahren',
            'Einmal durchlesen reicht',
          ],
          correctIndex: 1,
          explanation:
            'Eine Unterweisung ist nur wirksam, wenn die Inhalte verstanden und im Alltag konsequent umgesetzt werden.',
        },
      ],
    },
  ],
}
